import { Component, ViewChild } from '@angular/core';
import { FormArray, FormControl, FormGroup, NonNullableFormBuilder, Validators } from '@angular/forms';
import { NgbActiveModal, NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import moment from 'moment';
import { Subject, takeUntil } from 'rxjs';
import { AuthService } from '../../../../auth/auth.service';
import { UserDto } from '../../../../auth/user.dto';
import { ConfirmationComponent } from '../../../../shared/confirmation/confirmation.component';
import { DynamicFormComponent } from '../../../../shared/dynamic-form/dynamic-form.component';
import { FieldOptions } from '../../../../shared/dynamic-form/interfaces/field-options.interface';
import { DisplayLabel } from '../../../../shared/models/displayLabel';
import { ContractEngagementType } from '../../../../shared/models/enums/contract-engagement-type.enum';
import { MaritalStatus } from '../../../../shared/models/enums/marital-status.enum';
import { Nationality, labelsByNationality } from '../../../../shared/models/enums/nationality.enum';
import { ResidentialStatus, ResidentialStatusLabel } from '../../../../shared/models/enums/residential-status.enum';
import { AssetService } from '../../../../shared/services/asset.service';
import { BeneficiariesService } from '../../../../shared/services/beneficiaries.service';
import { BeneficiaryEmploymentService } from '../../../../shared/services/beneficiary-employment.service';
import { BeneficiaryComplementsService } from '../../../../shared/services/beneficiaryComplements.service';
import { DegreeService } from '../../../../shared/services/degree.service';
import { JobService } from '../../../../shared/services/job.service';
import { QPVService } from '../../../../shared/services/qpv.service.ts';
import { StringUtils } from '../../../../shared/services/stringUtils.service';
import { StructureService } from '../../../../shared/services/structure.service';
import { TrainingAreaService } from '../../../../shared/services/trainingArea.service';
import { TransportModeService } from '../../../../shared/services/transport-mode.service';
import { ToastService } from '../../../../shared/toast/toast.service';
import { Utils } from '../../../../shared/utils';
import { DateValidators } from '../../../../shared/validators/date.validators';
import { BeneficiaryComplement } from '../../../beneficiary-details/complement/beneficiaryComplement';
import { BeneficiaryEmploymentDto } from '../../../beneficiary-details/employment/dto/beneficiary-employment.dto';
import { JobCategoryDto } from '../../../beneficiary-details/employment/dto/job-category.dto';
import { BeneficiaryDto } from '../../../dto/beneficiary.dto';
import { ContractWrapperComponent } from '../../contract/contract-wrapper/contract-wrapper.component';
import { ContractDto } from '../../contract/contract.dto';
import { FseDetailInitialData, FseDetailsComponent } from '../../fse/fse-details/fse-details.component';
import { AssessmentSignatureComponent } from '../assessment-signature/assessment-signature.component';
import {
  AssessmentActivity,
  AssessmentContext,
  AssessmentContractTypes,
  AssessmentExperienceLength,
  AssessmentHowLongHasWorked,
  AssessmentInChargePersonCategory,
  AssessmentInChargePersonCategorySort,
  AssessmentInChargePersonSituation,
  AssessmentJobCenterRegistration,
  AssessmentProExpDuration,
  AssessmentStatus,
  AssessmentSupportProposal,
  AssessmentUnactivity,
  AssessmentWorkingTime,
  assessmentActivityKeyFromLabel,
  assessmentActivityLabels,
  assessmentActivitySort,
  assessmentContextLabels,
  assessmentContextSort,
  assessmentContractTypesLabels,
  assessmentContractTypesSort,
  assessmentExperienceLengthLabels,
  assessmentExperienceLengthSort,
  assessmentHowLongHasWorkedLabels,
  assessmentHowLongHasWorkedSort,
  assessmentInChargePersonCategoryLabels,
  assessmentInChargePersonSituationLabels,
  assessmentInChargePersonSituationSort,
  assessmentJobCenterRegistrationLabels,
  assessmentJobCenterRegistrationSort,
  assessmentProExpDurationLabels,
  assessmentProExpDurationSort,
  assessmentStatusLabels,
  assessmentSupportProposalLabels,
  assessmentSupportProposalSort,
  assessmentUnactivityKeyFromLabel,
  assessmentUnactivityLabels,
  assessmentUnactivitySort,
  assessmentWorkingTimeLabels,
  assessmentWorkingTimeSort,
  residentialStatusLabels,
} from '../assessment.enums';
import { AssessmentService } from '../assessment.service';
import { AssessmentDto } from './assessment.dto';

// *********************************************************************************************
// * TYPES TYPES TYPES TYPES TYPES TYPES TYPES TYPES TYPES TYPES TYPES TYPES TYPES TYPES TYPES *
// * TYPES TYPES TYPES TYPES TYPES TYPES TYPES TYPES TYPES TYPES TYPES TYPES TYPES TYPES TYPES *
// *********************************************************************************************

enum ActionSave {
  openModalSurveyFse = 'openModalSurveyFse',
  openModalContractEngagement = 'openModalContractEngagement',
}

type AssessmentFormGroup = FormGroup<{
  creationDateInterview: FormControl<Date>;
  status: FormControl<AssessmentStatus>;
  updateStatusDate: FormControl<Date>;
  context: FormControl<AssessmentContext>;

  // Sub forms
  detailForm: DetailFormGroup;
  globalSituationForm: GlobalSituationFormGroup;
  orientationForm: OrientationFormGroup;
  supportForm: SupportFormGroup;
}>;

type DetailFormGroup = FormGroup<{
  checkedBenefPersonalData: FormControl<boolean>;

  nationality: FormControl<Nationality>;

  validIdentityDocument: FormControl<boolean>;
  renewalInProgress: FormControl<boolean>;
  renewalHelpNeeded: FormControl<boolean>;

  singleParent: FormControl<boolean>;

  adminDifficulties: FormControl<boolean>;
  adminBlocking: FormControl<boolean>;
  adminHelpForRights: FormControl<boolean>;
  adminJuridBlocking: FormControl<boolean>;
  adminTaxNotice: FormControl<boolean>;

  readingWritingDifficulties: FormControl<boolean>;
  readingWritingWrittenFrenchDifficulty: FormControl<boolean>;
  readingWritingReadingFrenchDifficulty: FormControl<boolean>;
  readingWritingOralFrenchDifficulty: FormControl<boolean>;
  readingWritingMathDifficulty: FormControl<boolean>;
  readingWritingFrenchAsForeignLanguage: FormControl<boolean>;
  readingWritingLearningDifficulty: FormControl<boolean>;
  readingWritingHasFrenchSupportForProcedures: FormControl<boolean>;
  readingWritingLanguageAssessmentDone: FormControl<boolean>;
  readingWritingLanguageAssessmentDoneDate: FormControl<Date>;
  readingWritingLanguageAssessmentDoneResult: FormControl<string>;
  readingWritingLanguageLevelSufficientForCareer: FormControl<string>;

  childInProgress: FormControl<boolean>;
  isHelper: FormControl<boolean>;

  familyBarriers: FormControl<boolean>;
  familyBarriersDetails: FormControl<string>;
  familyBarriersHasChildUnder3WithoutChildcare: FormControl<boolean>;
  familyBarriersHasChildcareArrangements: FormControl<boolean>;
  familyBarriersHasSearchedForChildcare: FormControl<boolean>;
  familyBarriersHasSearchedForChildcareDetails: FormControl<string>;
  familyBarriersHasChildWithDisability: FormControl<boolean>;
  familyBarriersHasChildWithHealthIssues: FormControl<boolean>;
  familyBarriersHasDependentParent: FormControl<boolean>;
  familyBarriersReceivesSupportForFamilyCare: FormControl<boolean>;
  familyBarriersHasTimeConstraintsForChildPickup: FormControl<boolean>;
  familyBarriersHasTimeConstraintsForChildPickupDetails: FormControl<string>;
  familyBarriersHasOtherFamilyConstraints: FormControl<boolean>;
  familyBarriersWantsParentingAdvice: FormControl<boolean>;

  accreditationSIAE: FormControl<boolean>;
  accreditationSIAEDate: FormControl<Date>;

  accreditationRQTH: FormControl<string>;
  accreditationRQTHDate: FormControl<Date>;

  accreditationAAH: FormControl<string>;
  accreditationAAHDate: FormControl<Date>;

  health: FormControl<boolean>;
  healthCoverageAccessOrUpdate: FormControl<boolean>;
  healthHasHealthCoverage: FormControl<boolean>;
  healthDailyHealthDifficulty: FormControl<boolean>;
  healthLimitsSomeJobs: FormControl<boolean>;
  healthBlocksImmediateWorkReturn: FormControl<boolean>;
  healthCausesRegularAbsences: FormControl<boolean>;
  healthHasMedicalFollowUp: FormControl<boolean>;
  healthMedicalAccess: FormControl<boolean>;
  healthWantsSupportForCareAccess: FormControl<boolean>;
  healthLacksAwarenessOfIssue: FormControl<boolean>;
  healthStrugglesToIntegrateIssueInPath: FormControl<boolean>;
}>;

type GlobalSituationFormGroup = FormGroup<{
  claimRsaInsufRes: FormControl<boolean>;
  claimRsaNoRes: FormControl<boolean>;
  claimRsaNoMoreUnemployRights: FormControl<boolean>;
  claimRsaNoMoreUnemploy: FormControl<boolean>;
  claimRsaRefugeeClaimant: FormControl<boolean>;
  claimRsaOther: FormControl<boolean>;
  claimRsaOtherComment: FormControl<string>;

  financialDifficulties: FormControl<boolean>;
  financialDifficultiesHasOtherResourcesThanRSA: FormControl<boolean>;
  financialDifficultiesAidNeeded: FormControl<boolean>;
  financialDifficultiesAidNeededForRent: FormControl<boolean>;
  financialDifficultiesAidNeededForEnergy: FormControl<boolean>;
  financialDifficultiesAidNeededForFood: FormControl<boolean>;
  financialDifficultiesAidNeededForFines: FormControl<boolean>;
  financialDifficultiesAidNeededForOther: FormControl<boolean>;
  financialDifficultiesHasDebtOrOverindebtedness: FormControl<boolean>;
  financialDifficultiesHasBudgetManagementDifficulty: FormControl<boolean>;
  financialDifficultiesIsBelowPovertyThreshold: FormControl<boolean>;
  financialDifficultiesReceivesThirdPartySupport: FormControl<boolean>;
  financialDifficultiesHasBankingAccessIssue: FormControl<boolean>;

  residentialStatus: FormControl<ResidentialStatus>;
  socialExclusion: FormControl<boolean>;
  indebtedness: FormControl<boolean>;

  housing: FormControl<boolean>;
  housingInadequate: FormControl<boolean>;
  housingUnsanitary: FormControl<boolean>;
  housingEvictionSoon: FormControl<boolean>;
  housingEvictionSoonDetails: FormControl<string>;
  housingSearchInProgress: FormControl<boolean>;
  housingSearchInProgressDetails: FormControl<string>;
  housingSearchWithSupport: FormControl<boolean>;

  autonomousMobility: FormControl<boolean>;
  licenceB: FormControl<string>;
  tclSubscription: FormControl<string>;

  transportModes: FormArray<
    FormGroup<{
      transportModeId: FormControl<number>;
      description: FormControl<string>;
    }>
  >;

  maxMobilityDistance: FormControl<number>;
  maxMobilityGoToWorkTime: FormControl<number>;

  mobility: FormControl<boolean>;
  mobilityDifficulties: FormControl<boolean>;
  mobilityDifficultiesNoTransportAvailable: FormControl<boolean>;
  mobilityDifficultiesDependentOnPublicTransport: FormControl<boolean>;
  mobilityDifficultiesCanRideBike: FormControl<boolean>;
  mobilityDifficultiesOwnBikeOrSharedBike: FormControl<boolean>;
  mobilityDifficultiesNeedsDrivingLicenseForJob: FormControl<boolean>;
  mobilityDifficultiesHasForeignLicenseOutsideEEE: FormControl<boolean>;
  mobilityDifficultiesHealthPreventsDriving: FormControl<boolean>;
  mobilityDifficultiesHealthLimitsOverallMobility: FormControl<boolean>;
  mobilityDifficultiesNeedsAdaptedDrivingLicense: FormControl<boolean>;
  mobilityDifficultiesDifficultyReadingInformation: FormControl<boolean>;
  mobilityDifficultiesStressWhenTraveling: FormControl<boolean>;
  mobilityDifficultiesUnfamiliarWithTCLApps: FormControl<boolean>;
  mobilityDifficultiesPoorSenseOfDirection: FormControl<boolean>;
  mobilityDifficultiesUnawareOfExistingTransportOptions: FormControl<boolean>;

  numericDifficulties: FormControl<boolean>;
  numericDifficultiesHasDevice: FormControl<boolean>;
  numericDifficultiesHasComputer: FormControl<boolean>;
  numericDifficultiesHasSmartphone: FormControl<boolean>;
  numericDifficultiesHasTablet: FormControl<boolean>;
  numericDifficultiesUsesDeviceAlone: FormControl<boolean>;
  numericDifficultiesHasInternetAccess: FormControl<boolean>;
  numericDifficultiesHasEmail: FormControl<boolean>;
  numericDifficultiesHasAccessibleDevice: FormControl<boolean>;
  numericDifficultiesHasFranceConnect: FormControl<boolean>;
  numericDifficultiesHasDigitalID: FormControl<boolean>;
  numericDifficultiesAdministrativeProcessMethod: FormControl<string>;
  numericDifficultiesHasDifficultyWithOnlineProcedures: FormControl<boolean>;
  numericDifficultiesHasPublicAccessToTech: FormControl<boolean>;
  numericDifficultiesKnowsHelpLocations: FormControl<boolean>;

  assistance: FormControl<boolean>;
  assistedByConseiller: FormControl<boolean>;
  assistedByAssociation: FormControl<boolean>;
  assistedByTravailleurSocial: FormControl<boolean>;
  assistedByFamilleAmis: FormControl<boolean>;
  assistedByAutre: FormControl<boolean>;

  socialSatisfaction: FormControl<boolean>;
  socialSelfCareTime: FormControl<string>;
  socialSelfCareActivities: FormControl<string>;
  socialGroupComfort: FormControl<string>;
  socialNewPeopleStress: FormControl<string>;
}>;

type OrientationFormGroup = FormGroup<{
  degreeId: FormControl<number>;
  studyAbroad: FormControl<boolean>;
  degreeLabel: FormControl<string>;
  degreeGained: FormControl<boolean>;
  trainingAreaIds: FormControl<number[]>;

  formations: FormArray<
    FormGroup<{
      label: FormControl<string>;
      beginDate: FormControl<Date>;
      endDate: FormControl<Date>;
    }>
  >;

  isActive: FormControl<boolean>;
  activity: FormControl<AssessmentActivity>;
  unactivity: FormControl<AssessmentUnactivity>;

  hasWorked: FormControl<boolean>;
  howLongHasWorked: FormControl<AssessmentHowLongHasWorked>;

  proExps: FormArray<
    FormGroup<{
      contractType: FormControl<AssessmentContractTypes>;
      job: FormControl<string>;
      duration: FormControl<AssessmentProExpDuration>;
      beginDate: FormControl<Date>;
      endDate: FormControl<Date>;
    }>
  >;

  searchingForJob: FormControl<boolean>;
  searchingForJobReasonNotTo: FormControl<string>;
  readyForNewJob: FormControl<boolean>;
  readyForNewJobReasonNotTo: FormControl<string>;
  jobCenterRegistration: FormControl<AssessmentJobCenterRegistration>;
  jobCenterLastUpdateDate: FormControl<Date>;
  integrationStepsTaken: FormControl<string>;

  hasProfessionnalProject: FormControl<boolean>;
  professionnalProjectDetails: FormControl<string>;
  professionalProjectKnowsHowToConductJobSearch: FormControl<boolean>;
  professionalProjectRequiresTraining: FormControl<boolean>;
  professionalProjectCanFollowUpOnTraining: FormControl<boolean>;
  professionalProjectKnowsJobSearchTechniques: FormControl<boolean>;
  professionalProjectHasDefinedCreationProject: FormControl<boolean>;

  accessToFormation: FormControl<boolean>;

  contractSearchType: FormControl<AssessmentContractTypes>;
  workingTime: FormControl<AssessmentWorkingTime>;

  jobSoughts: FormArray<
    FormGroup<{
      famillyWantedJobType: FormControl<JobCategoryDto>;
      domainWantedJobType: FormControl<JobCategoryDto>;
      wantedJobType: FormControl<JobCategoryDto>;
      experienceLength: FormControl<AssessmentExperienceLength>;
    }>
  >;

  expectationsRegardingRSA: FormControl<string>;

  advantages: FormArray<
    FormGroup<{
      assetId: FormControl<DisplayLabel>;
      description: FormControl<string>;
    }>
  >;
}>;

type SupportFormGroup = FormGroup<{
  supportProposal: FormControl<AssessmentSupportProposal>;
  supportAcceptance: FormControl<boolean>;
  dataSharingAcceptance: FormControl<boolean>;
  supportChoiceExplanation: FormControl<string>;
}>;

// *********************************************************************************************
// * CLASS CLASS CLASS CLASS CLASS CLASS CLASS CLASS CLASS CLASS CLASS CLASS CLASS CLASS CLASS *
// * CLASS CLASS CLASS CLASS CLASS CLASS CLASS CLASS CLASS CLASS CLASS CLASS CLASS CLASS CLASS *
// *********************************************************************************************

@Component({
  selector: 'app-assessment-details',
  templateUrl: './assessment-details.component.html',
  styleUrls: ['./assessment-details.component.scss'],
  standalone: false,
})
export class AssessmentDetailsComponent {
  stringAscOrder = StringUtils.stringAscOrder;
  ActionSave = ActionSave;

  private readonly destroy$: Subject<void> = new Subject();

  public readonly StringUtils: StringUtils = StringUtils;

  public user: UserDto | undefined = undefined;
  public userCanUpdate: boolean = false;

  public assessment: AssessmentDto;
  public beneficiary: BeneficiaryDto;
  public beneficiaryComplement: BeneficiaryComplement;
  public beneficiaryEmployment: BeneficiaryEmploymentDto;

  public supportAvailable: boolean = false;

  // Forms
  public assessmentForm: AssessmentFormGroup;
  public detailForm: DetailFormGroup;
  public globalSituationForm: GlobalSituationFormGroup;
  public orientationForm: OrientationFormGroup;
  public supportForm: SupportFormGroup;

  public submitted: boolean = false;

  // Sub-accordions
  adminSubAccordionCollapse: boolean = true;
  readingWritingDifficultiesSubAccordionCollapse: boolean = true;
  familyBarriersSubAccordionCollapse: boolean = true;
  healthSubAccordionCollapse: boolean = true;
  financialDifficultiesSubAccordionCollapse: boolean = true;
  housingSubAccordionCollapse: boolean = true;
  mobilityDifficultiesSubAccordionCollapse: boolean = true;
  numericDifficultiesSubAccordionCollapse: boolean = true;
  socialSatisfactionSubAccordionCollapse: boolean = true;
  professionalProjectSubAccordionCollapse: boolean = true;

  // Form-arrays
  @ViewChild('inChargePersons', { static: false }) public inChargePersons: DynamicFormComponent;
  @ViewChild('proExps', { static: false }) public proExps: DynamicFormComponent;
  @ViewChild('formations', { static: false }) public formations: DynamicFormComponent;
  @ViewChild('transportModes', { static: false }) public transportModes: DynamicFormComponent;
  @ViewChild('jobSoughts', { static: false }) public jobSoughts: DynamicFormComponent;
  @ViewChild('advantages', { static: false }) public advantages: DynamicFormComponent;

  // List selects
  public readonly statusMap: Map<AssessmentStatus, string> = assessmentStatusLabels;

  public readonly contextMap: Map<AssessmentContext, string> = assessmentContextLabels;
  assessmentContextSort = assessmentContextSort;

  public readonly inChargePersonCategoryMap: Map<AssessmentInChargePersonCategory, string> = assessmentInChargePersonCategoryLabels;
  assessmentInChargePersonCategorySort = AssessmentInChargePersonCategorySort;

  public readonly inChargePersonSituationMap: Map<AssessmentInChargePersonSituation, string> = assessmentInChargePersonSituationLabels;
  assessmentInChargePersonSituationSort = assessmentInChargePersonSituationSort;

  public readonly activityMap: Map<AssessmentActivity, string> = assessmentActivityLabels;
  assessmentActivitySort = assessmentActivitySort;

  public readonly unactivityMap: Map<AssessmentUnactivity, string> = assessmentUnactivityLabels;
  assessmentUnactivitySort = assessmentUnactivitySort;

  public readonly howLongHasWorkedMap: Map<AssessmentHowLongHasWorked, string> = assessmentHowLongHasWorkedLabels;
  assessmentHowLongHasWorkedSort = assessmentHowLongHasWorkedSort;

  public readonly proExpDurationMap: Map<AssessmentProExpDuration, string> = assessmentProExpDurationLabels;
  assessmentProExpDurationSort = assessmentProExpDurationSort;

  public readonly jobCenterRegistrationMap: Map<AssessmentJobCenterRegistration, string> = assessmentJobCenterRegistrationLabels;
  assessmentJobCenterRegistrationSort = assessmentJobCenterRegistrationSort;

  public readonly experienceLengthMap: Map<AssessmentExperienceLength, string> = assessmentExperienceLengthLabels;
  assessmentExperienceLengthSort = assessmentExperienceLengthSort;

  public readonly contractTypesMap: Map<AssessmentContractTypes, string> = assessmentContractTypesLabels;
  assessmentContractTypesSort = assessmentContractTypesSort;

  public readonly workingTimeMap: Map<AssessmentWorkingTime, string> = assessmentWorkingTimeLabels;
  assessmentWorkingTimeSort = assessmentWorkingTimeSort;

  public readonly residentialStatusMap: Map<ResidentialStatusLabel, string> = residentialStatusLabels;

  public readonly supportProposalMap: Map<AssessmentSupportProposal, string> = assessmentSupportProposalLabels;
  assessmentSupportProposalSort = assessmentSupportProposalSort;

  public transportModeMap: DisplayLabel[];

  public qpvs: DisplayLabel[] = [];
  labelsByNationality = labelsByNationality;
  nationality = Nationality;
  maritalStatuses = MaritalStatus;

  public inChargePersonsFields: (string | FieldOptions)[] = [];
  public proExpsFields: (string | FieldOptions)[] = [];
  public formationsFields: (string | FieldOptions)[] = [];
  public transportModesFields: (string | FieldOptions)[] = [];
  public jobSoughtsFields: (string | FieldOptions)[] = [];
  public advantagesFields: (string | FieldOptions)[] = [];

  public degrees: DisplayLabel[] = [];
  public trainingAreas: DisplayLabel[] = [];

  public sectors: JobCategoryDto[] = [];
  public employmentJobs: {
    famillyWantedJobType: JobCategoryDto;
    domainWantedJobType: JobCategoryDto;
    wantedJobType: JobCategoryDto;
    experienceLength: string;
  }[] = [];

  public assets: DisplayLabel[] = [];

  constructor(
    private readonly assessmentService: AssessmentService,
    private readonly assetService: AssetService,
    private readonly authService: AuthService,
    private readonly beneficiaryService: BeneficiariesService,
    private readonly beneficiaryComplementsService: BeneficiaryComplementsService,
    private readonly beneficiaryEmploymentService: BeneficiaryEmploymentService,
    private readonly degreeService: DegreeService,
    private readonly formBuilder: NonNullableFormBuilder,
    private readonly modal: NgbActiveModal,
    private readonly modalService: NgbModal,
    private readonly qpvService: QPVService,
    private readonly toastService: ToastService,
    private readonly structureService: StructureService,
    private readonly trainingAreaService: TrainingAreaService,
    private readonly transportModeService: TransportModeService,
    private readonly jobService: JobService,
  ) {}

  public async onInit(beneficiaryId?: number, assessment?: AssessmentDto): Promise<void> {
    this.assessment = assessment ? assessment : new AssessmentDto();
    this.beneficiary = await this.beneficiaryService.findById(beneficiaryId);
    this.beneficiaryComplement = await this.beneficiaryComplementsService.findById(this.beneficiary.complementId);
    if (this.beneficiary.employmentId) {
      this.beneficiaryEmployment = await this.beneficiaryEmploymentService.findById(this.beneficiary.employmentId);
    }

    this.assessmentService.assessmentCreated$.pipe(takeUntil(this.destroy$)).subscribe((assessment: AssessmentDto) => {
      this.assessment = assessment;
    });

    // Get user and verify authorized actions
    this.user = await this.authService.getUser();
    this.userCanUpdate = this.user.groups.some((group: string) => ['ADMIN', 'STRUCTURE', 'DIRECTION+', 'TERRITORY'].includes(group));

    this.qpvs = await this.qpvService.findAll();
    this.sectors = await this.jobService.findAll();
    if (this.beneficiaryEmployment && !this.assessment.id) {
      this.beneficiaryEmployment.jobIds.forEach((jobId) => {
        (this.sectors || []).forEach((sector) => {
          (sector.subValues || []).forEach((domain) => {
            (domain.subValues || []).forEach((job) => {
              if (job.id === jobId) {
                this.employmentJobs.push({ famillyWantedJobType: sector, domainWantedJobType: domain, wantedJobType: job, experienceLength: null });
              }
            });
          });
        });
      });
    }

    await this.loadForm();

    this.degrees = await this.degreeService.findAll();
    this.trainingAreas = await this.trainingAreaService.findAll();

    this.transportModeMap = await this.transportModeService.findAll();

    this.assets = await this.assetService.findAll();

    this.inChargePersonsFields = this.getInChargePersonsFields();
    this.proExpsFields = this.getProExpsFields();
    this.formationsFields = this.getFormationsFields();
    this.transportModesFields = this.getTransportModesFields();
    this.jobSoughtsFields = this.getJobSoughtsFields();
    this.advantagesFields = this.getAdvantagesFields();
  }

  // *********************************************************************************************
  // * FORMS FORMS FORMS FORMS FORMS FORMS FORMS FORMS FORMS FORMS FORMS FORMS FORMS FORMS FORMS *
  // * FORMS FORMS FORMS FORMS FORMS FORMS FORMS FORMS FORMS FORMS FORMS FORMS FORMS FORMS FORMS *
  // *********************************************************************************************

  // *********************************************************************************************
  // * Global

  protected get fcAss() {
    return this.assessmentForm.controls;
  }

  protected get fvAss() {
    return this.assessmentForm.value;
  }

  protected get frAss() {
    return this.assessmentForm.getRawValue();
  }

  private async loadForm(): Promise<void> {
    this.loadDetailForm();
    this.loadGlobalSituationForm();
    this.loadOrientationForm();
    this.loadSupportForm();
    this.assessmentForm = this.formBuilder.group({
      creationDateInterview: [this.assessment.creationDateInterview ? new Date(this.assessment.creationDateInterview) : new Date(), [Validators.required, DateValidators.dateFormatValidator]],
      status: [this.assessment.status, [Validators.required]],
      updateStatusDate: [{ value: this.assessment.updateStatusDate ? new Date(this.assessment.updateStatusDate) : null, disabled: true }],
      context: [this.assessment.context, [Validators.required]],

      // Sub forms
      detailForm: this.detailForm,
      globalSituationForm: this.globalSituationForm,
      orientationForm: this.orientationForm,
      supportForm: this.supportForm,
    });

    if (!this.userCanUpdate) {
      this.assessmentForm.disable();
    }

    // Update support availability when context changes
    this.onContextChange(this.fvAss.context);
    this.assessmentForm.controls.context.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value: AssessmentContext) => this.onContextChange(value));
  }

  public onStatusChange(): void {
    this.fcAss.updateStatusDate.setValue(this.assessment?.status !== this.fcAss.status.value ? new Date() : new Date(this.assessment.updateStatusDate));
    this.assessmentForm.get('updateStatusDate').updateValueAndValidity();
  }

  public onContextChange(value: AssessmentContext): void {
    this.supportAvailable = !([AssessmentContext.BeginningAssessment, AssessmentContext.AmendAssessment] as string[]).includes(assessmentContextLabels.get(value));
  }

  // *********************************************************************************************
  // * Detail

  protected get fcDet() {
    return this.detailForm.controls;
  }

  protected get fvDet() {
    return this.detailForm.value;
  }

  protected get frDet() {
    return this.detailForm.getRawValue();
  }

  private loadDetailForm(): void {
    this.detailForm = this.formBuilder.group({
      checkedBenefPersonalData: [this.assessment.checkedBenefPersonalData || <any>null, Validators.required],

      nationality: [{ value: this.assessment.nationality ?? (this.beneficiary.nationality as Nationality), disabled: true }],

      validIdentityDocument: [this.assessment.validIdentityDocument],
      renewalInProgress: [this.assessment.renewalInProgress],
      renewalHelpNeeded: [this.assessment.renewalHelpNeeded],

      singleParent: [this.assessment.singleParent ?? this.beneficiaryComplement.singleParent],

      adminDifficulties: [this.assessment.adminDifficulties],
      adminBlocking: [this.assessment.adminBlocking],
      adminHelpForRights: [this.assessment.adminHelpForRights],
      adminJuridBlocking: [this.assessment.adminJuridBlocking],
      adminTaxNotice: [this.assessment.adminTaxNotice],

      readingWritingDifficulties: [this.assessment.readingWritingDifficulties],
      readingWritingWrittenFrenchDifficulty: [this.assessment.readingWritingWrittenFrenchDifficulty],
      readingWritingReadingFrenchDifficulty: [this.assessment.readingWritingReadingFrenchDifficulty],
      readingWritingOralFrenchDifficulty: [this.assessment.readingWritingOralFrenchDifficulty],
      readingWritingMathDifficulty: [this.assessment.readingWritingMathDifficulty],
      readingWritingFrenchAsForeignLanguage: [this.assessment.readingWritingFrenchAsForeignLanguage],
      readingWritingLearningDifficulty: [this.assessment.readingWritingLearningDifficulty],
      readingWritingHasFrenchSupportForProcedures: [this.assessment.readingWritingHasFrenchSupportForProcedures],
      readingWritingLanguageAssessmentDone: [this.assessment.readingWritingLanguageAssessmentDone],
      readingWritingLanguageAssessmentDoneDate: [this.assessment.readingWritingLanguageAssessmentDoneDate],
      readingWritingLanguageAssessmentDoneResult: [this.assessment.readingWritingLanguageAssessmentDoneResult],
      readingWritingLanguageLevelSufficientForCareer: [this.assessment.readingWritingLanguageLevelSufficientForCareer],

      childInProgress: [this.assessment.childInProgress],
      isHelper: [this.assessment.isHelper],

      familyBarriers: [this.assessment.familyBarriers],
      familyBarriersDetails: [this.assessment.familyBarriersDetails],
      familyBarriersHasChildUnder3WithoutChildcare: [this.assessment.familyBarriersHasChildUnder3WithoutChildcare],
      familyBarriersHasChildcareArrangements: [this.assessment.familyBarriersHasChildcareArrangements],
      familyBarriersHasSearchedForChildcare: [this.assessment.familyBarriersHasSearchedForChildcare],
      familyBarriersHasSearchedForChildcareDetails: [this.assessment.familyBarriersHasSearchedForChildcareDetails],
      familyBarriersHasChildWithDisability: [this.assessment.familyBarriersHasChildWithDisability],
      familyBarriersHasChildWithHealthIssues: [this.assessment.familyBarriersHasChildWithHealthIssues],
      familyBarriersHasDependentParent: [this.assessment.familyBarriersHasDependentParent],
      familyBarriersReceivesSupportForFamilyCare: [this.assessment.familyBarriersReceivesSupportForFamilyCare],
      familyBarriersHasTimeConstraintsForChildPickup: [this.assessment.familyBarriersHasTimeConstraintsForChildPickup],
      familyBarriersHasTimeConstraintsForChildPickupDetails: [this.assessment.familyBarriersHasTimeConstraintsForChildPickupDetails],
      familyBarriersHasOtherFamilyConstraints: [this.assessment.familyBarriersHasOtherFamilyConstraints],
      familyBarriersWantsParentingAdvice: [this.assessment.familyBarriersWantsParentingAdvice],

      accreditationSIAE: [this.assessment.accreditationSIAE ?? this.beneficiaryEmployment?.accreditationSIAE],
      accreditationSIAEDate: [(this.assessment.accreditationSIAEDate ?? this.beneficiaryEmployment?.accreditationSIAEDate) ? (this.assessment.accreditationSIAEDate ?? this.beneficiaryEmployment?.accreditationSIAEDate) : { value: null, disabled: true }],

      accreditationRQTH: [this.assessment.accreditationRQTH ?? (this.beneficiaryEmployment?.rqth === true ? 'Yes' : this.beneficiaryEmployment?.rqth === false ? 'No' : null)],
      accreditationRQTHDate: [(this.assessment.accreditationRQTHDate ?? this.beneficiaryEmployment?.expiryRqthDate) ? (this.assessment.accreditationRQTHDate ?? this.beneficiaryEmployment?.expiryRqthDate) : { value: null, disabled: true }],

      accreditationAAH: [this.assessment.accreditationAAH],
      accreditationAAHDate: [this.assessment.accreditationAAHDate ? this.assessment.accreditationAAHDate : { value: null, disabled: true }],

      health: [this.assessment.health],
      healthCoverageAccessOrUpdate: [this.assessment.healthCoverageAccessOrUpdate],
      healthHasHealthCoverage: [this.assessment.healthHasHealthCoverage],
      healthDailyHealthDifficulty: [this.assessment.healthDailyHealthDifficulty],
      healthLimitsSomeJobs: [this.assessment.healthLimitsSomeJobs],
      healthBlocksImmediateWorkReturn: [this.assessment.healthBlocksImmediateWorkReturn],
      healthCausesRegularAbsences: [this.assessment.healthCausesRegularAbsences],
      healthHasMedicalFollowUp: [this.assessment.healthHasMedicalFollowUp],
      healthMedicalAccess: [this.assessment.healthMedicalAccess],
      healthWantsSupportForCareAccess: [this.assessment.healthWantsSupportForCareAccess],
      healthLacksAwarenessOfIssue: [this.assessment.healthLacksAwarenessOfIssue],
      healthStrugglesToIntegrateIssueInPath: [this.assessment.healthStrugglesToIntegrateIssueInPath],
    });

    this.fcDet.validIdentityDocument.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value: boolean) => {
      this.fcDet.renewalInProgress.setValue(null);
      this.fcDet.renewalHelpNeeded.setValue(null);
    });

    this.fcDet.checkedBenefPersonalData.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value: boolean) => {
      this.fcDet.checkedBenefPersonalData.setValue(value || null, { emitEvent: false });
    });

    this.onAccreditationSIAEChange(this.fvDet.accreditationSIAE);
    this.fcDet.accreditationSIAE.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value: boolean) => {
      this.onAccreditationSIAEChange(value);
    });

    this.onAccreditationRQTHChange(this.fvDet.accreditationRQTH);
    this.fcDet.accreditationRQTH.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value: string) => {
      this.onAccreditationRQTHChange(value);
    });

    this.onAccreditationAAHChange(this.fvDet.accreditationAAH);
    this.fcDet.accreditationAAH.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value: string) => {
      this.onAccreditationAAHChange(value);
    });
  }

  private onAccreditationSIAEChange(value: boolean): void {
    if (!value) {
      this.fcDet.accreditationSIAEDate.setValue(null);
    }
    this.fcDet.accreditationSIAEDate[value ? 'enable' : 'disable']();
    this.detailForm.updateValueAndValidity();
  }

  private onAccreditationRQTHChange(value: string): void {
    if (value !== 'Yes') {
      this.fcDet.accreditationRQTHDate.setValue(null);
    }
    this.fcDet.accreditationRQTHDate[value === 'Yes' ? 'enable' : 'disable']();
    this.detailForm.updateValueAndValidity();
  }

  private onAccreditationAAHChange(value: string): void {
    if (value !== 'Yes') {
      this.fcDet.accreditationAAHDate.setValue(null);
    }
    this.fcDet.accreditationAAHDate[value === 'Yes' ? 'enable' : 'disable']();
    this.detailForm.updateValueAndValidity();
  }

  // *********************************************************************************************
  // * Detail - Children

  private getInChargePersonsFields(): (string | FieldOptions)[] {
    return [
      { value: 'id', validators: [] },
      { value: 'category', validators: [Validators.required] },
      { value: 'firstName', validators: [Validators.required, Validators.maxLength(30)] },
      { value: 'lastName', validators: [Validators.required, Validators.maxLength(30)] },
      { value: 'situation', validators: [Validators.required] },
      { value: 'birthDate', validators: [Validators.required] },
    ];
  }

  // *********************************************************************************************
  // * Global situation

  protected get fcGSit() {
    return this.globalSituationForm.controls;
  }

  protected get fvGSit() {
    return this.globalSituationForm.value;
  }

  protected get frGSit() {
    return this.globalSituationForm.getRawValue();
  }

  private loadGlobalSituationForm(): void {
    this.globalSituationForm = this.formBuilder.group(
      {
        claimRsaInsufRes: [this.assessment.claimRsaInsufRes],
        claimRsaNoRes: [this.assessment.claimRsaNoRes],
        claimRsaNoMoreUnemployRights: [this.assessment.claimRsaNoMoreUnemployRights],
        claimRsaNoMoreUnemploy: [this.assessment.claimRsaNoMoreUnemploy],
        claimRsaRefugeeClaimant: [this.assessment.claimRsaRefugeeClaimant],
        claimRsaOther: [this.assessment.claimRsaOther],
        claimRsaOtherComment: this.assessment.claimRsaOther ? [this.assessment.claimRsaOtherComment, Validators.maxLength(30)] : [{ value: null, disabled: true }],

        financialDifficulties: [this.assessment.financialDifficulties],
        financialDifficultiesHasOtherResourcesThanRSA: [this.assessment.financialDifficultiesHasOtherResourcesThanRSA],
        financialDifficultiesAidNeeded: [this.assessment.financialDifficultiesAidNeeded],
        financialDifficultiesAidNeededForRent: [this.assessment.financialDifficultiesAidNeededForRent],
        financialDifficultiesAidNeededForEnergy: [this.assessment.financialDifficultiesAidNeededForEnergy],
        financialDifficultiesAidNeededForFood: [this.assessment.financialDifficultiesAidNeededForFood],
        financialDifficultiesAidNeededForFines: [this.assessment.financialDifficultiesAidNeededForFines],
        financialDifficultiesAidNeededForOther: [this.assessment.financialDifficultiesAidNeededForOther],
        financialDifficultiesHasDebtOrOverindebtedness: [this.assessment.financialDifficultiesHasDebtOrOverindebtedness],
        financialDifficultiesHasBudgetManagementDifficulty: [this.assessment.financialDifficultiesHasBudgetManagementDifficulty],
        financialDifficultiesIsBelowPovertyThreshold: [this.assessment.financialDifficultiesIsBelowPovertyThreshold],
        financialDifficultiesReceivesThirdPartySupport: [this.assessment.financialDifficultiesReceivesThirdPartySupport],
        financialDifficultiesHasBankingAccessIssue: [this.assessment.financialDifficultiesHasBankingAccessIssue],

        residentialStatus: [(this.assessment.residentialStatus ?? this.beneficiary.residentialAddress.residentialStatus) as ResidentialStatus],
        socialExclusion: [this.assessment.socialExclusion],
        indebtedness: [this.assessment.indebtedness],

        housing: [this.assessment.housing],
        housingInadequate: [this.assessment.housingInadequate],
        housingUnsanitary: [this.assessment.housingUnsanitary],
        housingEvictionSoon: [this.assessment.housingEvictionSoon],
        housingEvictionSoonDetails: [this.assessment.housingEvictionSoonDetails],
        housingSearchInProgress: [this.assessment.housingSearchInProgress],
        housingSearchInProgressDetails: [this.assessment.housingSearchInProgressDetails],
        housingSearchWithSupport: [this.assessment.housingSearchWithSupport],

        autonomousMobility: [this.assessment.autonomousMobility],
        licenceB: [this.assessment.licenceB],
        tclSubscription: [this.assessment.tclSubscription],

        transportModes: this.formBuilder.array(
          (this.assessment.transportModes?.length ? this.assessment.transportModes : this.beneficiaryEmployment?.transportModes.length ? this.beneficiaryEmployment.transportModes : []).map((transportMode) =>
            this.formBuilder.group({
              transportModeId: [transportMode.transportModeId],
              description: [transportMode.description],
            }),
          ),
        ),

        maxMobilityDistance: [this.assessment.maxMobilityDistance, Validators.pattern(Utils.integerPattern)],
        maxMobilityGoToWorkTime: [this.assessment.maxMobilityGoToWorkTime, Validators.pattern(Utils.integerPattern)],

        mobility: [this.assessment.mobility],
        mobilityDifficulties: [this.assessment.mobilityDifficulties],
        mobilityDifficultiesNoTransportAvailable: [this.assessment.mobilityDifficultiesNoTransportAvailable],
        mobilityDifficultiesDependentOnPublicTransport: [this.assessment.mobilityDifficultiesDependentOnPublicTransport],
        mobilityDifficultiesCanRideBike: [this.assessment.mobilityDifficultiesCanRideBike],
        mobilityDifficultiesOwnBikeOrSharedBike: [this.assessment.mobilityDifficultiesOwnBikeOrSharedBike],
        mobilityDifficultiesNeedsDrivingLicenseForJob: [this.assessment.mobilityDifficultiesNeedsDrivingLicenseForJob],
        mobilityDifficultiesHasForeignLicenseOutsideEEE: [this.assessment.mobilityDifficultiesHasForeignLicenseOutsideEEE],
        mobilityDifficultiesHealthPreventsDriving: [this.assessment.mobilityDifficultiesHealthPreventsDriving],
        mobilityDifficultiesHealthLimitsOverallMobility: [this.assessment.mobilityDifficultiesHealthLimitsOverallMobility],
        mobilityDifficultiesNeedsAdaptedDrivingLicense: [this.assessment.mobilityDifficultiesNeedsAdaptedDrivingLicense],
        mobilityDifficultiesDifficultyReadingInformation: [this.assessment.mobilityDifficultiesDifficultyReadingInformation],
        mobilityDifficultiesStressWhenTraveling: [this.assessment.mobilityDifficultiesStressWhenTraveling],
        mobilityDifficultiesUnfamiliarWithTCLApps: [this.assessment.mobilityDifficultiesUnfamiliarWithTCLApps],
        mobilityDifficultiesPoorSenseOfDirection: [this.assessment.mobilityDifficultiesPoorSenseOfDirection],
        mobilityDifficultiesUnawareOfExistingTransportOptions: [this.assessment.mobilityDifficultiesUnawareOfExistingTransportOptions],

        numericDifficulties: [this.assessment.numericDifficulties],
        numericDifficultiesHasDevice: [this.assessment.numericDifficultiesHasDevice],
        numericDifficultiesHasComputer: [this.assessment.numericDifficultiesHasComputer],
        numericDifficultiesHasSmartphone: [this.assessment.numericDifficultiesHasSmartphone],
        numericDifficultiesHasTablet: [this.assessment.numericDifficultiesHasTablet],
        numericDifficultiesUsesDeviceAlone: [this.assessment.numericDifficultiesUsesDeviceAlone],
        numericDifficultiesHasInternetAccess: [this.assessment.numericDifficultiesHasInternetAccess],
        numericDifficultiesHasEmail: [this.assessment.numericDifficultiesHasEmail],
        numericDifficultiesHasAccessibleDevice: [this.assessment.numericDifficultiesHasAccessibleDevice],
        numericDifficultiesHasFranceConnect: [this.assessment.numericDifficultiesHasFranceConnect],
        numericDifficultiesHasDigitalID: [this.assessment.numericDifficultiesHasDigitalID],
        numericDifficultiesAdministrativeProcessMethod: [this.assessment.numericDifficultiesAdministrativeProcessMethod],
        numericDifficultiesHasDifficultyWithOnlineProcedures: [this.assessment.numericDifficultiesHasDifficultyWithOnlineProcedures],
        numericDifficultiesHasPublicAccessToTech: [this.assessment.numericDifficultiesHasPublicAccessToTech],
        numericDifficultiesKnowsHelpLocations: [this.assessment.numericDifficultiesKnowsHelpLocations],

        assistance: [this.assessment.assistance],
        assistedByConseiller: [this.assessment.assistedByConseiller],
        assistedByAssociation: [this.assessment.assistedByAssociation],
        assistedByTravailleurSocial: [this.assessment.assistedByTravailleurSocial],
        assistedByFamilleAmis: [this.assessment.assistedByFamilleAmis],
        assistedByAutre: [this.assessment.assistedByAutre],

        socialSatisfaction: [this.assessment.socialSatisfaction],
        socialSelfCareTime: [this.assessment.socialSelfCareTime],
        socialSelfCareActivities: [this.assessment.socialSelfCareActivities],
        socialGroupComfort: [this.assessment.socialGroupComfort],
        socialNewPeopleStress: [this.assessment.socialNewPeopleStress],
      },
      {
        // validators: [DateValidators.datesOrderValidator('beginDateLastJob', 'endDateLastJob')],
      },
    );

    this.fcGSit.claimRsaOther.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value: boolean) => {
      this.fcGSit.claimRsaOtherComment.setValue(null);
      this.fcGSit.claimRsaOtherComment.setValidators(value ? [Validators.maxLength(30)] : []);
      this.fcGSit.claimRsaOtherComment[value ? 'enable' : 'disable']();
      this.globalSituationForm.updateValueAndValidity();
    });

    this.fcGSit.assistance.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value: boolean) => {
      if (!value) {
        this.fcGSit.assistedByConseiller.setValue(null);
        this.fcGSit.assistedByAssociation.setValue(null);
        this.fcGSit.assistedByTravailleurSocial.setValue(null);
        this.fcGSit.assistedByFamilleAmis.setValue(null);
        this.fcGSit.assistedByAutre.setValue(null);
        this.globalSituationForm.updateValueAndValidity();
      }
    });
  }

  // *********************************************************************************************
  // * Global situation - Pro exps

  private getProExpsFields(): (string | FieldOptions)[] {
    return [
      { value: 'id', validators: [] },
      { value: 'contractType', validators: [Validators.required] },
      { value: 'job', validators: [Validators.required] },
      { value: 'duration', validators: [Validators.required] },
      { value: 'beginDate', validators: [] },
      { value: 'endDate', validators: [] },
    ];
  }

  // *********************************************************************************************
  // * Global situation - Formations

  private getFormationsFields(): (string | FieldOptions)[] {
    return [
      { value: 'id', validators: [] },
      { value: 'label', validators: [Validators.required] },
      { value: 'beginDate', validators: [] },
      { value: 'endDate', validators: [] },
    ];
  }

  // *********************************************************************************************
  // * Social situation - Transport modes

  private getTransportModesFields(): (string | FieldOptions)[] {
    return [
      { value: 'id', validators: [] },
      { value: 'transportModeId', validators: [Validators.required] },
      { value: 'description', validators: [] },
    ];
  }

  // *********************************************************************************************
  // * Orientation

  protected get fcOri() {
    return this.orientationForm.controls;
  }

  protected get fvOri() {
    return this.orientationForm.value;
  }

  protected get frOri() {
    return this.orientationForm.getRawValue();
  }

  private loadOrientationForm(): void {
    this.initJobSoughtData();

    this.orientationForm = this.formBuilder.group({
      degreeId: [this.assessment.degree?.id || this.beneficiaryEmployment?.degreeId],
      studyAbroad: [this.assessment.studyAbroad !== undefined ? this.assessment.studyAbroad : this.beneficiaryEmployment?.studyAbroad],
      degreeLabel: [this.assessment.degreeLabel],
      degreeGained: [this.assessment.degreeGained],
      trainingAreaIds: [this.assessment.trainingAreas?.map((trainingArea) => trainingArea.id) || this.beneficiaryEmployment?.trainingAreaIds],

      formations: this.formBuilder.array(
        this.assessment.formations.map((formation) =>
          this.formBuilder.group({
            label: [formation.label],
            beginDate: [formation.beginDate],
            endDate: [formation.endDate],
          }),
        ),
      ),

      isActive: [this.assessment.isActive],
      activity: [this.assessment.activity],
      unactivity: [this.assessment.unactivity],

      hasWorked: [this.assessment.hasWorked],
      howLongHasWorked: [this.assessment.howLongHasWorked],

      proExps: this.formBuilder.array(
        this.assessment.proExps.map((proExp) =>
          this.formBuilder.group({
            contractType: [proExp.contractType],
            job: [proExp.job],
            duration: [proExp.duration],
            beginDate: [proExp.beginDate],
            endDate: [proExp.endDate],
          }),
        ),
      ),

      searchingForJob: [this.assessment.searchingForJob],
      searchingForJobReasonNotTo: [this.assessment.searchingForJobReasonNotTo],
      readyForNewJob: [this.assessment.readyForNewJob],
      readyForNewJobReasonNotTo: [this.assessment.readyForNewJobReasonNotTo],
      jobCenterRegistration: [this.assessment.jobCenterRegistration ?? (this.beneficiary.lastUpdateDate ? AssessmentJobCenterRegistration.Yes : null)],
      jobCenterLastUpdateDate: [
        this.assessment.jobCenterLastUpdateDate ??
          this.beneficiary.lastUpdateDate ?? {
            value: null,
            disabled: this.assessment.jobCenterRegistration || this.beneficiary.jobCenterNumber,
          },
      ],
      integrationStepsTaken: [this.assessment.integrationStepsTaken],

      hasProfessionnalProject: [this.assessment.hasProfessionnalProject],
      professionnalProjectDetails: [this.assessment.professionnalProjectDetails],
      professionalProjectKnowsHowToConductJobSearch: [this.assessment.professionalProjectKnowsHowToConductJobSearch],
      professionalProjectRequiresTraining: [this.assessment.professionalProjectRequiresTraining],
      professionalProjectCanFollowUpOnTraining: [this.assessment.professionalProjectCanFollowUpOnTraining],
      professionalProjectKnowsJobSearchTechniques: [this.assessment.professionalProjectKnowsJobSearchTechniques],
      professionalProjectHasDefinedCreationProject: [this.assessment.professionalProjectHasDefinedCreationProject],

      accessToFormation: [this.assessment.accessToFormation],

      contractSearchType: [this.assessment.contractSearchType],
      workingTime: [this.assessment.workingTime],

      jobSoughts: this.formBuilder.array(
        this.assessment.jobSoughts.map((jobSought) =>
          this.formBuilder.group({
            famillyWantedJobType: [],
            domainWantedJobType: [],
            wantedJobType: [],
            experienceLength: [],
          }),
        ),
      ),

      expectationsRegardingRSA: [this.assessment.expectationsRegardingRSA],

      advantages: this.formBuilder.array(
        this.assessment.advantages.map((_advantage) =>
          this.formBuilder.group({
            assetId: [],
            description: [],
          }),
        ),
      ),
    });

    this.fcOri.isActive.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value: boolean) => {
      this.fcOri.activity.setValue(null);
      this.fcOri.unactivity.setValue(null);
    });

    this.fcOri.hasWorked.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value: boolean) => {
      this.fcOri.howLongHasWorked.setValue(null);
    });

    this.fcOri.searchingForJob.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value: boolean) => {
      this.fcOri.searchingForJobReasonNotTo.setValue(null);
      this.globalSituationForm.updateValueAndValidity();
    });

    this.fcOri.readyForNewJob.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value: boolean) => {
      this.fcOri.readyForNewJobReasonNotTo.setValue(null);
      this.globalSituationForm.updateValueAndValidity();
    });

    this.fcOri.jobCenterRegistration.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value: AssessmentJobCenterRegistration) => {
      this.fcOri.jobCenterLastUpdateDate.setValue(assessmentJobCenterRegistrationLabels.get(value) === AssessmentJobCenterRegistration.Yes ? this.beneficiary.lastUpdateDate : null);
      this.fcOri.jobCenterLastUpdateDate[assessmentJobCenterRegistrationLabels.get(value) === AssessmentJobCenterRegistration.Yes ? 'enable' : 'disable']();
      this.globalSituationForm.updateValueAndValidity();
    });
  }

  private initJobSoughtData(): void {
    this.assessment.jobSoughts.map((jobSought) => {
      this.employmentJobs.push({
        famillyWantedJobType: this.sectors.find((sector) => sector.id === jobSought.famillyWantedJobTypeId),
        domainWantedJobType: this.sectors.find((sector) => sector.id === jobSought.famillyWantedJobTypeId)?.subValues?.find((domain) => domain.id === jobSought.domainWantedJobTypeId),
        wantedJobType: this.sectors
          .find((sector) => sector.id === jobSought.famillyWantedJobTypeId)
          ?.subValues?.find((domain) => domain.id === jobSought.domainWantedJobTypeId)
          ?.subValues.find((job) => job.id === jobSought.wantedJobTypeId),
        experienceLength: jobSought.experienceLength,
      });
    });
  }

  // *********************************************************************************************
  // * Orientation - jobs soughts

  private getJobSoughtsFields(): (string | FieldOptions)[] {
    return [
      { value: 'id', validators: [] },
      { value: 'famillyWantedJobType', validators: [Validators.required] },
      { value: 'domainWantedJobType', validators: [Validators.required] },
      { value: 'wantedJobType', validators: [Validators.required] },
      { value: 'experienceLength', validators: [] },
    ];
  }

  // *********************************************************************************************
  // * Orientation - assets

  private getAdvantagesFields(): (string | FieldOptions)[] {
    return [
      { value: 'id', validators: [] },
      { value: 'assetId', validators: [Validators.required] },
      { value: 'description', validators: [Validators.required] },
    ];
  }

  // *********************************************************************************************
  // * Orientation - jobs soughts fields update

  public readonly handleSectorChange: (group: FormGroup) => void = (group: FormGroup): void => {
    group.controls.domainWantedJobType.setValue(undefined);
  };

  public readonly handleDomainChange: (group: FormGroup) => void = (group: FormGroup): void => {
    if (group.controls.domainWantedJobType.value?.subValues?.length === 1) {
      group.controls.wantedJobType.setValue(group.controls.domainWantedJobType.value?.subValues[0]);
    } else {
      group.controls.wantedJobType.setValue(undefined);
    }
  };

  // *********************************************************************************************
  // * Support

  protected get fcSup() {
    return this.supportForm.controls;
  }

  protected get fvSup() {
    return this.supportForm.value;
  }

  protected get frSup() {
    return this.supportForm.getRawValue();
  }

  private loadSupportForm(): void {
    this.supportForm = this.formBuilder.group({
      supportProposal: [this.assessment.supportProposal],
      supportAcceptance: [this.assessment.supportAcceptance],
      dataSharingAcceptance: [this.assessment.dataSharingAcceptance],
      supportChoiceExplanation: [this.assessment.supportChoiceExplanation],
    });
  }

  // *********************************************************************************************
  // * SAVE SAVE SAVE SAVE SAVE SAVE SAVE SAVE SAVE SAVE SAVE SAVE SAVE SAVE SAVE SAVE SAVE SAVE *
  // * SAVE SAVE SAVE SAVE SAVE SAVE SAVE SAVE SAVE SAVE SAVE SAVE SAVE SAVE SAVE SAVE SAVE SAVE *
  // *********************************************************************************************

  public async save(action?: ActionSave): Promise<void> {
    this.submitted = true;

    if (!this.assessmentForm.valid || !this.inChargePersons.form.valid || !this.proExps.form.valid || !this.formations.form.valid || !this.transportModes.form.valid || !this.jobSoughts.form.valid || !this.advantages.form.valid) {
      this.toastService.show('La sauvegarde a échoué, veuillez vérifier les données.', 'danger');
    } else {
      try {
        const assessmentId = await this.assessmentService.saveAssessment({
          id: this.assessment.id,
          beneficiaryId: this.assessment.beneficiaryId || this.beneficiary.id,
          status: this.fvAss.status,
          creationDateInterview: moment.utc(this.fvAss.creationDateInterview).format('YYYY-MM-DD'),
          context: this.fvAss.context,

          // Details
          checkedBenefPersonalData: this.fvDet.checkedBenefPersonalData,
          nationality: this.frDet.nationality || null,
          validIdentityDocument: this.fvDet.validIdentityDocument,
          renewalInProgress: this.fvDet.renewalInProgress,
          renewalHelpNeeded: this.fvDet.renewalHelpNeeded,

          // Familial situation
          singleParent: this.fvDet.singleParent,

          adminDifficulties: this.fvDet.adminDifficulties,
          adminBlocking: this.fvDet.adminBlocking,
          adminHelpForRights: this.fvDet.adminHelpForRights,
          adminJuridBlocking: this.fvDet.adminJuridBlocking,
          adminTaxNotice: this.fvDet.adminTaxNotice,

          readingWritingDifficulties: this.fvDet.readingWritingDifficulties,
          readingWritingWrittenFrenchDifficulty: this.fvDet.readingWritingWrittenFrenchDifficulty,
          readingWritingReadingFrenchDifficulty: this.fvDet.readingWritingReadingFrenchDifficulty,
          readingWritingOralFrenchDifficulty: this.fvDet.readingWritingOralFrenchDifficulty,
          readingWritingMathDifficulty: this.fvDet.readingWritingMathDifficulty,
          readingWritingFrenchAsForeignLanguage: this.fvDet.readingWritingFrenchAsForeignLanguage,
          readingWritingLearningDifficulty: this.fvDet.readingWritingLearningDifficulty,
          readingWritingHasFrenchSupportForProcedures: this.fvDet.readingWritingHasFrenchSupportForProcedures,
          readingWritingLanguageAssessmentDone: this.fvDet.readingWritingLanguageAssessmentDone,
          readingWritingLanguageAssessmentDoneDate: this.fvDet.readingWritingLanguageAssessmentDoneDate,
          readingWritingLanguageAssessmentDoneResult: this.fvDet.readingWritingLanguageAssessmentDoneResult,
          readingWritingLanguageLevelSufficientForCareer: this.fvDet.readingWritingLanguageLevelSufficientForCareer,

          // In charge persons
          inChargePersons: this.inChargePersons.form.controls.items.value.map((value) => ({
            id: value.id,
            category: value.category,
            firstName: value.firstName,
            lastName: value.lastName,
            age: value.age,
            situation: value.situation,
            comment: value.comment || null,
            liveAtFamilyHome: value.liveAtFamilyHome,
            birthDate: value.birthDate,
          })),

          childInProgress: this.fvDet.childInProgress,
          isHelper: this.fvDet.isHelper,

          familyBarriers: this.fvDet.familyBarriers,
          familyBarriersDetails: this.fvDet.familyBarriersDetails || null,
          familyBarriersHasChildUnder3WithoutChildcare: this.fvDet.familyBarriersHasChildUnder3WithoutChildcare,
          familyBarriersHasChildcareArrangements: this.fvDet.familyBarriersHasChildcareArrangements,
          familyBarriersHasSearchedForChildcare: this.fvDet.familyBarriersHasSearchedForChildcare,
          familyBarriersHasSearchedForChildcareDetails: this.fvDet.familyBarriersHasSearchedForChildcareDetails || null,
          familyBarriersHasChildWithDisability: this.fvDet.familyBarriersHasChildWithDisability,
          familyBarriersHasChildWithHealthIssues: this.fvDet.familyBarriersHasChildWithHealthIssues,
          familyBarriersHasDependentParent: this.fvDet.familyBarriersHasDependentParent,
          familyBarriersReceivesSupportForFamilyCare: this.fvDet.familyBarriersReceivesSupportForFamilyCare,
          familyBarriersHasTimeConstraintsForChildPickup: this.fvDet.familyBarriersHasTimeConstraintsForChildPickup,
          familyBarriersHasTimeConstraintsForChildPickupDetails: this.fvDet.familyBarriersHasTimeConstraintsForChildPickupDetails || null,
          familyBarriersHasOtherFamilyConstraints: this.fvDet.familyBarriersHasOtherFamilyConstraints,
          familyBarriersWantsParentingAdvice: this.fvDet.familyBarriersWantsParentingAdvice,

          accreditationSIAE: this.fvDet.accreditationSIAE,
          accreditationSIAEDate: this.fvDet.accreditationSIAEDate ?? null,

          accreditationRQTH: this.fvDet.accreditationRQTH,
          accreditationRQTHDate: this.fvDet.accreditationRQTHDate ?? null,

          accreditationAAH: this.fvDet.accreditationAAH,
          accreditationAAHDate: this.fvDet.accreditationAAHDate ?? null,

          health: this.fvDet.health,
          healthCoverageAccessOrUpdate: this.fvDet.healthCoverageAccessOrUpdate,
          healthHasHealthCoverage: this.fvDet.healthHasHealthCoverage,
          healthDailyHealthDifficulty: this.fvDet.healthDailyHealthDifficulty,
          healthLimitsSomeJobs: this.fvDet.healthLimitsSomeJobs,
          healthBlocksImmediateWorkReturn: this.fvDet.healthBlocksImmediateWorkReturn,
          healthCausesRegularAbsences: this.fvDet.healthCausesRegularAbsences,
          healthHasMedicalFollowUp: this.fvDet.healthHasMedicalFollowUp,
          healthMedicalAccess: this.fvDet.healthMedicalAccess,
          healthWantsSupportForCareAccess: this.fvDet.healthWantsSupportForCareAccess,
          healthLacksAwarenessOfIssue: this.fvDet.healthLacksAwarenessOfIssue,
          healthStrugglesToIntegrateIssueInPath: this.fvDet.healthStrugglesToIntegrateIssueInPath,

          // Global situation
          claimRsaInsufRes: this.fvGSit.claimRsaInsufRes,
          claimRsaNoRes: this.fvGSit.claimRsaNoRes,
          claimRsaNoMoreUnemployRights: this.fvGSit.claimRsaNoMoreUnemployRights,
          claimRsaNoMoreUnemploy: this.fvGSit.claimRsaNoMoreUnemploy,
          claimRsaRefugeeClaimant: this.fvGSit.claimRsaRefugeeClaimant,
          claimRsaOther: this.fvGSit.claimRsaOther,
          claimRsaOtherComment: this.fvGSit.claimRsaOtherComment || null,

          financialDifficulties: this.fvGSit.financialDifficulties,
          financialDifficultiesHasOtherResourcesThanRSA: this.fvGSit.financialDifficultiesHasOtherResourcesThanRSA,
          financialDifficultiesAidNeeded: this.fvGSit.financialDifficultiesAidNeeded,
          financialDifficultiesAidNeededForRent: this.fvGSit.financialDifficultiesAidNeededForRent,
          financialDifficultiesAidNeededForEnergy: this.fvGSit.financialDifficultiesAidNeededForEnergy,
          financialDifficultiesAidNeededForFood: this.fvGSit.financialDifficultiesAidNeededForFood,
          financialDifficultiesAidNeededForFines: this.fvGSit.financialDifficultiesAidNeededForFines,
          financialDifficultiesAidNeededForOther: this.fvGSit.financialDifficultiesAidNeededForOther,
          financialDifficultiesHasDebtOrOverindebtedness: this.fvGSit.financialDifficultiesHasDebtOrOverindebtedness,
          financialDifficultiesHasBudgetManagementDifficulty: this.fvGSit.financialDifficultiesHasBudgetManagementDifficulty,
          financialDifficultiesIsBelowPovertyThreshold: this.fvGSit.financialDifficultiesIsBelowPovertyThreshold,
          financialDifficultiesReceivesThirdPartySupport: this.fvGSit.financialDifficultiesReceivesThirdPartySupport,
          financialDifficultiesHasBankingAccessIssue: this.fvGSit.financialDifficultiesHasBankingAccessIssue,

          residentialStatus: this.fvGSit.residentialStatus || null,
          socialExclusion: this.fvGSit.socialExclusion ?? null,
          indebtedness: this.fvGSit.indebtedness ?? null,

          housing: this.fvGSit.housing,
          housingInadequate: this.fvGSit.housingInadequate,
          housingUnsanitary: this.fvGSit.housingUnsanitary,
          housingEvictionSoon: this.fvGSit.housingEvictionSoon,
          housingEvictionSoonDetails: this.fvGSit.housingEvictionSoonDetails || null,
          housingSearchInProgress: this.fvGSit.housingSearchInProgress,
          housingSearchInProgressDetails: this.fvGSit.housingSearchInProgressDetails || null,
          housingSearchWithSupport: this.fvGSit.housingSearchWithSupport,

          autonomousMobility: this.fvGSit.autonomousMobility,
          licenceB: this.fvGSit.licenceB,
          tclSubscription: this.fvGSit.tclSubscription,

          transportModes: this.transportModes.form.controls.items.value.map((value) => ({
            id: value.id,
            transportModeId: value.transportModeId || null,
            description: value.description || null,
          })),

          maxMobilityDistance: this.fvGSit.maxMobilityDistance || null,
          maxMobilityGoToWorkTime: this.fvGSit.maxMobilityGoToWorkTime || null,

          mobility: this.fvGSit.mobility,
          mobilityDifficulties: this.fvGSit.mobilityDifficulties,
          mobilityDifficultiesNoTransportAvailable: this.fvGSit.mobilityDifficultiesNoTransportAvailable,
          mobilityDifficultiesDependentOnPublicTransport: this.fvGSit.mobilityDifficultiesDependentOnPublicTransport,
          mobilityDifficultiesCanRideBike: this.fvGSit.mobilityDifficultiesCanRideBike,
          mobilityDifficultiesOwnBikeOrSharedBike: this.fvGSit.mobilityDifficultiesOwnBikeOrSharedBike,
          mobilityDifficultiesNeedsDrivingLicenseForJob: this.fvGSit.mobilityDifficultiesNeedsDrivingLicenseForJob,
          mobilityDifficultiesHasForeignLicenseOutsideEEE: this.fvGSit.mobilityDifficultiesHasForeignLicenseOutsideEEE,
          mobilityDifficultiesHealthPreventsDriving: this.fvGSit.mobilityDifficultiesHealthPreventsDriving,
          mobilityDifficultiesHealthLimitsOverallMobility: this.fvGSit.mobilityDifficultiesHealthLimitsOverallMobility,
          mobilityDifficultiesNeedsAdaptedDrivingLicense: this.fvGSit.mobilityDifficultiesNeedsAdaptedDrivingLicense,
          mobilityDifficultiesDifficultyReadingInformation: this.fvGSit.mobilityDifficultiesDifficultyReadingInformation,
          mobilityDifficultiesStressWhenTraveling: this.fvGSit.mobilityDifficultiesStressWhenTraveling,
          mobilityDifficultiesUnfamiliarWithTCLApps: this.fvGSit.mobilityDifficultiesUnfamiliarWithTCLApps,
          mobilityDifficultiesPoorSenseOfDirection: this.fvGSit.mobilityDifficultiesPoorSenseOfDirection,
          mobilityDifficultiesUnawareOfExistingTransportOptions: this.fvGSit.mobilityDifficultiesUnawareOfExistingTransportOptions,

          numericDifficulties: this.fvGSit.numericDifficulties,
          numericDifficultiesHasDevice: this.fvGSit.numericDifficultiesHasDevice,
          numericDifficultiesHasComputer: this.fvGSit.numericDifficultiesHasComputer,
          numericDifficultiesHasSmartphone: this.fvGSit.numericDifficultiesHasSmartphone,
          numericDifficultiesHasTablet: this.fvGSit.numericDifficultiesHasTablet,
          numericDifficultiesUsesDeviceAlone: this.fvGSit.numericDifficultiesUsesDeviceAlone,
          numericDifficultiesHasInternetAccess: this.fvGSit.numericDifficultiesHasInternetAccess,
          numericDifficultiesHasEmail: this.fvGSit.numericDifficultiesHasEmail,
          numericDifficultiesHasAccessibleDevice: this.fvGSit.numericDifficultiesHasAccessibleDevice,
          numericDifficultiesHasFranceConnect: this.fvGSit.numericDifficultiesHasFranceConnect,
          numericDifficultiesHasDigitalID: this.fvGSit.numericDifficultiesHasDigitalID,
          numericDifficultiesAdministrativeProcessMethod: this.fvGSit.numericDifficultiesAdministrativeProcessMethod || null,
          numericDifficultiesHasDifficultyWithOnlineProcedures: this.fvGSit.numericDifficultiesHasDifficultyWithOnlineProcedures,
          numericDifficultiesHasPublicAccessToTech: this.fvGSit.numericDifficultiesHasPublicAccessToTech,
          numericDifficultiesKnowsHelpLocations: this.fvGSit.numericDifficultiesKnowsHelpLocations,

          assistance: this.fvGSit.assistance ?? null,
          assistedByConseiller: this.fvGSit.assistedByConseiller,
          assistedByAssociation: this.fvGSit.assistedByAssociation,
          assistedByTravailleurSocial: this.fvGSit.assistedByTravailleurSocial,
          assistedByFamilleAmis: this.fvGSit.assistedByFamilleAmis,
          assistedByAutre: this.fvGSit.assistedByAutre,

          socialSatisfaction: this.fvGSit.socialSatisfaction,
          socialSelfCareTime: this.fvGSit.socialSelfCareTime || null,
          socialSelfCareActivities: this.fvGSit.socialSelfCareActivities || null,
          socialGroupComfort: this.fvGSit.socialGroupComfort || null,
          socialNewPeopleStress: this.fvGSit.socialNewPeopleStress || null,

          // Orientation

          // Degree
          degree: this.fvOri.degreeId ? { id: this.fvOri.degreeId } : null,
          studyAbroad: this.fvOri.studyAbroad,
          degreeLabel: this.fvOri.degreeLabel || null,
          degreeGained: this.fvOri.degreeGained,
          trainingAreas: this.fvOri.trainingAreaIds ? this.fvOri.trainingAreaIds.map((id) => ({ id: id })) : null,

          formations: this.formations.form.controls.items.value.map((value) => ({
            id: value.id,
            label: value.label || null,
            beginDate: value.beginDate,
            endDate: value.endDate,
          })),

          isActive: this.fvOri.isActive,
          activity: this.fvOri.activity,
          unactivity: this.fvOri.unactivity,

          hasWorked: this.fvOri.hasWorked,
          howLongHasWorked: this.fvOri.howLongHasWorked,

          // Job
          proExps: this.proExps.form.controls.items.value.map((value) => ({
            id: value.id,
            contractType: value.contractType || null,
            job: value.job || null,
            duration: value.duration,
            beginDate: value.beginDate,
            endDate: value.endDate,
          })),
          searchingForJob: this.fvOri.searchingForJob,
          searchingForJobReasonNotTo: this.fvOri.searchingForJobReasonNotTo || null,
          readyForNewJob: this.fvOri.readyForNewJob,
          readyForNewJobReasonNotTo: this.fvOri.readyForNewJobReasonNotTo || null,
          jobCenterRegistration: this.fvOri.jobCenterRegistration,
          jobCenterLastUpdateDate: this.fvOri.jobCenterLastUpdateDate,
          integrationStepsTaken: this.fvOri.integrationStepsTaken || null,

          hasProfessionnalProject: this.fvOri.hasProfessionnalProject,
          professionnalProjectDetails: this.fvOri.professionnalProjectDetails || null,
          professionalProjectKnowsHowToConductJobSearch: this.fvOri.professionalProjectKnowsHowToConductJobSearch,
          professionalProjectRequiresTraining: this.fvOri.professionalProjectRequiresTraining,
          professionalProjectCanFollowUpOnTraining: this.fvOri.professionalProjectCanFollowUpOnTraining,
          professionalProjectKnowsJobSearchTechniques: this.fvOri.professionalProjectKnowsJobSearchTechniques,
          professionalProjectHasDefinedCreationProject: this.fvOri.professionalProjectHasDefinedCreationProject,

          accessToFormation: this.fvOri.accessToFormation,

          contractSearchType: this.fvOri.contractSearchType || null,
          workingTime: this.fvOri.workingTime || null,

          jobSoughts: this.jobSoughts.form.controls.items.value.map((value) => ({
            id: value.id,
            famillyWantedJobTypeId: value.famillyWantedJobType.id || null,
            domainWantedJobTypeId: value.domainWantedJobType.id || null,
            wantedJobTypeId: value.wantedJobType.id || null,
            experienceLength: value.experienceLength || null,
          })),

          expectationsRegardingRSA: this.fvOri.expectationsRegardingRSA || null,

          advantages: this.advantages.form.controls.items.value,

          // Support
          supportProposal: this.fvSup.supportProposal || null,
          supportAcceptance: this.fvSup.supportAcceptance,
          dataSharingAcceptance: this.fvSup.dataSharingAcceptance,
          supportChoiceExplanation: this.fvSup.supportChoiceExplanation || null,
        } as AssessmentDto);

        this.toastService.show('Le diagnostic a été enregistré avec succès.', 'success');

        if (action == ActionSave.openModalSurveyFse) {
          await this.openFseSurveyModal(this.frAss.updateStatusDate.getFullYear().toString(), this.user?.groups.includes('STRUCTURE') ? this.user.structureId : (await this.structureService.findAllByIdIodas('METROPOLE DE LYON'))[0]?.id, moment.utc(this.fvAss.creationDateInterview).format('YYYY-MM-DD'));
        }
        if (action == ActionSave.openModalContractEngagement) {
          await this.openContractModal(assessmentId);
        }

        this.modal.close();
      } catch (error) {
        if (error.status === 440) {
          this.toastService.show('Veuillez enregistrer une signature dans votre profil.', 'danger');
        }
      }
    }
  }

  public cancel(): void {
    this.modal.close();
  }

  public async delete(): Promise<void> {
    const confirm: boolean = await this.openConfirmationModal();
    if (confirm) {
      this.modal.close();
      await this.assessmentService.delete(this.assessment.id);
    }
  }

  public async duplicate(): Promise<void> {
    const newAssesment = { ...this.assessment };
    newAssesment.id = null;
    newAssesment.status = null;
    newAssesment.updateStatusDate = null;
    newAssesment.context = null;
    newAssesment.creationDateInterview = moment.utc(new Date()).format('YYYY-MM-DD');
    newAssesment.generatedSurveyFse = null;

    const modal: NgbModalRef = this.modalService.open(AssessmentDetailsComponent, {
      windowClass: 'rightModal',
      size: 'xl',
      backdrop: 'static',
    });
    const modalComponent: AssessmentDetailsComponent = modal.componentInstance as AssessmentDetailsComponent;
    await modalComponent.onInit(this.beneficiary.id, newAssesment);
    this.cancel();
  }

  private async openConfirmationModal(): Promise<boolean> {
    const modal: NgbModalRef = this.modalService.open(ConfirmationComponent, {
      backdrop: 'static',
      centered: true,
    });
    const modalComponent: ConfirmationComponent = modal.componentInstance as ConfirmationComponent;
    return await modalComponent.confirm('Êtes-vous sûr(e) de vouloir supprimer ce diagnostic ?');
  }

  public async syncAssessment(): Promise<void> {
    const assessmentInit: AssessmentDto = await this.assessmentService.getInitByBeneficiaryId(this.assessment.beneficiaryId);
    // this.assessmentForm.controls.title.setValue(this.assessmentInit.title);
    // Etc.
  }

  // *********************************************************************************************
  // * Open FSE survey modal

  private async openFseSurveyModal(year: string, structureId: number, beginDate: string): Promise<void> {
    const modal: NgbModalRef = this.modalService.open(FseDetailsComponent, {
      windowClass: 'rightModal',
      size: 'lg',
      backdrop: 'static',
    });
    const modalComponent: FseDetailsComponent = modal.componentInstance as FseDetailsComponent;

    const initialData: FseDetailInitialData = {
      year: year,
      structureId: structureId,
      beginDate: beginDate,
      assessmentSourceId: this.assessment.id,
      entryQuestion7: this.beneficiary.birthCity === 'Ville étrangère' ? '7No' : !this.beneficiary.birthCity ? undefined : '7Yes',
      entryQuestion8: '8d',
      entryQuestion3: null,
    } as FseDetailInitialData;

    if (this.assessment.isActive === true) {
      switch (assessmentActivityKeyFromLabel(this.assessment.activity)) {
        case AssessmentActivity.CasualEmployment:
          initialData.entryQuestion1 = '1cYes';
          break;

        case AssessmentActivity.SelfEmployed:
          initialData.entryQuestion1 = '1aYes';
          break;

        case AssessmentActivity.Farmer:
          initialData.entryQuestion1 = '1aYes';
          break;

        case AssessmentActivity.Employee:
          initialData.entryQuestion1 = '1bYes';
          break;
      }
    } else if (this.assessment.isActive === false) {
      initialData.entryQuestion1 = '1No';
      if (assessmentUnactivityKeyFromLabel(this.assessment.unactivity) === AssessmentUnactivity.Formation) {
        initialData.entryQuestion1e = '1eYes';
      }
    }

    switch (this.assessment.jobCenterRegistration) {
      case AssessmentJobCenterRegistration.Yes:
        initialData.entryQuestion9 = '9Yes';
        initialData.entryQuestion9a = this.assessment.jobCenterLastUpdateDate;
        break;

      case AssessmentJobCenterRegistration.No:
        initialData.entryQuestion9 = '9No';
        break;

      case AssessmentJobCenterRegistration.InProgress:
        initialData.entryQuestion9 = '9No';
        break;
    }

    if (this.assessment.degree?.id === 2) {
      initialData.entryQuestion2 = '2b';
    } else if ([3, 4].includes(this.assessment.degree?.id)) {
      initialData.entryQuestion2 = '2c';
    } else if ([5, 6, 7, 8].includes(this.assessment.degree?.id)) {
      initialData.entryQuestion2 = '2d';
    }

    if (this.assessment.accreditationRQTH || this.assessment.accreditationAAH) {
      initialData.entryQuestion3 = '3Yes';
    }

    initialData.entryQuestion4 = '4Yes';
    if (this.assessment.accreditationAAH) {
      initialData.entryQuestion4c = '4cYes';
    }

    if (this.assessment.socialExclusion === true) {
      initialData.entryQuestion5 = '5Yes';
    } else if (this.assessment.socialExclusion === false) {
      initialData.entryQuestion5 = '5No';
    } else if (this.assessment.socialExclusion === null) {
      initialData.entryQuestion5 = '5DK';
    }

    await modalComponent.onInit(undefined, initialData);
  }

  // *********************************************************************************************
  // * Open contract modal
  private async openContractModal(assessmentId: number) {
    const modal: NgbModalRef = this.modalService.open(ContractWrapperComponent, {
      windowClass: 'rightModal',
      size: 'xl',
      backdrop: 'static',
    });
    const modalComponent: ContractWrapperComponent = modal.componentInstance as ContractWrapperComponent;
    await modalComponent.onInit(
      this.beneficiary,
      {
        assessmentId: assessmentId,
        assessment: await this.assessmentService.findById(assessmentId),
      } as ContractDto,
      Object.values(ContractEngagementType),
    );
  }

  // *********************************************************************************************
  // * Generate document by opening signature modal

  public async generateDocument(): Promise<void> {
    const signatureData: string = await this.openAssessmentSignatureModal();
    await this.assessmentService.generateDocument(this.assessment.id, signatureData);
  }

  private async openAssessmentSignatureModal(): Promise<any> {
    const modal: NgbModalRef = this.modalService.open(AssessmentSignatureComponent, {
      backdrop: 'static',
      centered: true,
      size: 'md',
    });
    const modalComponent: AssessmentSignatureComponent = modal.componentInstance as AssessmentSignatureComponent;
    modalComponent.assessment = this.assessment;
    return modalComponent.confirm();
  }

  // *********************************************************************************************
  // * UTILS UTILS UTILS UTILS UTILS UTILS UTILS UTILS UTILS UTILS UTILS UTILS UTILS UTILS UTILS *
  // * UTILS UTILS UTILS UTILS UTILS UTILS UTILS UTILS UTILS UTILS UTILS UTILS UTILS UTILS UTILS *
  // *********************************************************************************************

  @ViewChild('detailAccordion') detailAccordion!: HTMLDivElement;
  @ViewChild('globalSituationAccordion') globalSituationAccordion!: HTMLDivElement;
  @ViewChild('orientationAccordion') orientationAccordion!: HTMLDivElement;
  @ViewChild('supportAccordion') supportAccordion!: HTMLDivElement;

  scroll(el: HTMLDivElement): void {
    el.scrollIntoView({ behavior: 'smooth' });
  }
}
